<script setup>
// The Shop block's body, shared by both templates: a grid of products,
// each with a Buy button that opens the organization's own checkout
// (shop.js says why). The templates set the --shop-* variables.
import { computed, inject, onBeforeUnmount, onMounted, reactive, ref } from 'vue';
import { cardPrice, priceText, shopButtonLabel, shopColumns, shopItems } from './shop.js';
import CheckoutNotice from './CheckoutNotice.vue';
import ShopCart from './ShopCart.vue';
import { claimCart, releaseCart, useShopCart } from './useShopCart.js';
import { shippingFee } from './shopCart.js';

const props = defineProps({
    config: { type: Object, required: true },
    site_data: { type: Object, default: () => ({}) },
});

const editing = inject('tiqEditorCanvas', false);
// A product with no link of its own is bought by card on the
// organization's connected Stripe account, when there is one and the
// price is a number. Same tab: Stripe sends the buyer back here.
const cardCheckout = computed(() => Boolean(props.site_data?.site?.donations?.card_checkout));
const items = computed(() => shopItems(props.config).map((p) => (!p.has_link && cardCheckout.value && cardPrice(p.price)
    ? { ...p, card: true, provider: 'Stripe' }
    : { ...p, card: false })));
const columns = computed(() => shopColumns(props.config));
const buyLabel = computed(() => shopButtonLabel(props.config));
const currency = computed(() => String(props.site_data?.site?.donations?.currency || 'USD').toUpperCase());
const price = (p) => priceText(p.price, currency.value);

// ── The cart ──────────────────────────────────────────────────────
// Card products go into one cart and are paid for together. The size
// is picked HERE, because Stripe's page asks one dropdown question per
// checkout and a cart can hold several products with sizes.
const cart = useShopCart();
// The block's flat fee, charged once per order when it asks for an
// address. Shown under the products so nobody meets it first on Stripe.
const shipping = computed(() => (props.config.ship !== false ? shippingFee(props.config.shipping_fee) : ''));
const hasCardItems = computed(() => items.value.some((p) => p.card));
const sizes = reactive({});
const needsSize = reactive({});
const justAdded = ref(-1);
let addedTimer = null;
function addToCart(p, i) {
    if (editing) return;
    const option = p.options.length ? (sizes[i] || '') : '';
    if (p.options.length && !option) {
        needsSize[i] = true;
        return;
    }
    needsSize[i] = false;
    if (!cart.add({ name: p.name, option, price: p.price, image: p.image, shipping: shipping.value })) {
        needsSize[i] = 'full';
        return;
    }
    justAdded.value = i;
    clearTimeout(addedTimer);
    addedTimer = setTimeout(() => { justAdded.value = -1; }, 2500);
}

// One cart panel per page, however many Shop blocks: the first block
// to mount draws it. Its button takes this block's colors.
const root = ref(null);
const ownsCart = ref(false);
const cartColors = ref({ bg: '#1a1a1a', text: '#fff' });
const cartToken = Symbol('shop');
onMounted(() => {
    if (editing || !cardCheckout.value) return;
    ownsCart.value = claimCart(cartToken);
    if (ownsCart.value && root.value) {
        const cs = getComputedStyle(root.value);
        cartColors.value = {
            bg: cs.getPropertyValue('--shop-btn-bg').trim() || '#1a1a1a',
            text: cs.getPropertyValue('--shop-btn-text').trim() || '#fff',
        };
    }
});
onBeforeUnmount(() => {
    clearTimeout(addedTimer);
    releaseCart(cartToken);
});
</script>

<template>
    <div ref="root" class="tiq-shop" :class="`tiq-shop--cols-${columns}`">
        <ShopCart v-if="ownsCart" :currency="currency" :button-bg="cartColors.bg" :button-text="cartColors.text" />
        <CheckoutNotice kind="shop" />
        <div v-if="items.length" class="tiq-shop__grid">
            <article v-for="(p, i) in items" :key="i" class="tiq-shop__item" :class="{ 'is-sold-out': p.sold_out }">
                <div class="tiq-shop__photo">
                    <img v-if="p.image" :src="p.image" :alt="p.alt || p.name" loading="lazy" />
                    <svg v-else class="tiq-shop__placeholder" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round" aria-hidden="true">
                        <path d="M8.5 3.5 4 6l1.8 4 1.7-.8V20h9V9.2l1.7.8L20 6l-4.5-2.5a3.5 3.5 0 0 1-7 0z" />
                    </svg>
                    <span v-if="p.sold_out" class="tiq-shop__badge">Sold out</span>
                </div>
                <div class="tiq-shop__body">
                    <div class="tiq-shop__top">
                        <h3 class="tiq-shop__name">{{ p.name }}</h3>
                        <span v-if="price(p)" class="tiq-shop__price">{{ price(p) }}</span>
                    </div>
                    <p v-if="p.text" class="tiq-shop__text">{{ p.text }}</p>
                    <ul v-if="p.options.length && !(p.card && !p.sold_out)" class="tiq-shop__options" aria-label="Options">
                        <li v-for="o in p.options" :key="o">{{ o }}</li>
                    </ul>
                    <div class="tiq-shop__buy">
                        <span v-if="p.sold_out" class="tiq-shop__btn is-disabled" aria-disabled="true">Sold out</span>
                        <template v-else-if="p.card">
                            <label v-if="p.options.length" class="tiq-shop__size">
                                <span class="tiq-shop__size-label">Size</span>
                                <select :value="sizes[i] || ''" class="tiq-shop__select" :aria-label="`Choose a size for ${p.name}`"
                                        :aria-invalid="needsSize[i] === true ? 'true' : null"
                                        @change="sizes[i] = $event.target.value; needsSize[i] = false">
                                    <option value="" disabled>Choose one</option>
                                    <option v-for="o in p.options" :key="o" :value="o">{{ o }}</option>
                                </select>
                            </label>
                            <button type="button" class="tiq-shop__btn tiq-shop__btn--card tiq-shop-add" @click="addToCart(p, i)">
                                {{ justAdded === i ? 'Added to cart' : 'Add to cart' }}
                            </button>
                            <p v-if="needsSize[i] === true" class="tiq-shop__warn" role="alert">Choose a size first.</p>
                            <p v-else-if="needsSize[i] === 'full'" class="tiq-shop__warn" role="alert">Your cart is full. Check out, then add the rest.</p>
                            <button v-else-if="justAdded === i" type="button" class="tiq-shop__view" @click="cart.panelOpen.value = true">View cart</button>
                        </template>
                        <a v-else-if="p.href" class="tiq-shop__btn" :href="p.href" target="_blank" rel="noopener"
                           :aria-label="`${buyLabel} ${p.name} (opens checkout in a new tab)`">
                            {{ buyLabel }}
                            <svg viewBox="0 0 20 20" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M7 13 13 7M8 7h5v5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                        </a>
                        <span v-else-if="editing" class="tiq-shop__missing">{{ cardCheckout ? `Give it a price, or a checkout link, to show a ${buyLabel} button.` : `Add a checkout link in the editor to show a ${buyLabel} button.` }}</span>
                        <p v-if="p.href && !p.sold_out && p.provider && !p.card" class="tiq-shop__via">Checkout on {{ p.provider }}</p>
                    </div>
                </div>
            </article>
        </div>
        <p v-else-if="editing" class="tiq-shop__hint">Add a product in the editor: a photo, a name, a price and a checkout link.</p>
        <p v-if="shipping && hasCardItems" class="tiq-shop__note tiq-shop-shipping">Shipping: {{ priceText(shipping, currency) }} per order.</p>
        <p v-if="config.note && items.length" class="tiq-shop__note">{{ config.note }}</p>
    </div>
</template>

<style scoped>
.tiq-shop { --shop-min: 250px; container-type: inline-size; }
.tiq-shop--cols-2 { --shop-min: 340px; }
.tiq-shop--cols-4 { --shop-min: 200px; }
.tiq-shop__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(min(100%, var(--shop-min)), 1fr));
    gap: clamp(16px, 2.5vw, 28px);
}
.tiq-shop__item {
    min-width: 0;
    display: flex;
    flex-direction: column;
    background: var(--shop-card, #fff);
    border: 1px solid var(--shop-line, rgba(0, 0, 0, 0.1));
    border-radius: var(--shop-radius, 10px);
    overflow: hidden;
}
.tiq-shop__photo {
    position: relative;
    aspect-ratio: 1 / 1;
    background: var(--shop-photo-bg, #f1efe9);
    display: grid;
    place-items: center;
}
.tiq-shop__photo img { width: 100%; height: 100%; object-fit: cover; display: block; }
.tiq-shop__placeholder { width: 38%; height: 38%; color: var(--shop-muted, #9a978f); }
.tiq-shop__item.is-sold-out .tiq-shop__photo img { filter: grayscale(0.7); opacity: 0.75; }
.tiq-shop__badge {
    position: absolute;
    top: 12px;
    left: 12px;
    padding: 4px 10px;
    border-radius: 999px;
    background: rgba(20, 20, 20, 0.82);
    color: #fff;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
}
.tiq-shop__body { flex: 1; display: flex; flex-direction: column; gap: 10px; padding: 16px 18px 18px; }
.tiq-shop__top { display: flex; flex-wrap: wrap; align-items: baseline; justify-content: space-between; gap: 2px 12px; }
.tiq-shop__name { margin: 0; font-size: 18px; font-weight: 700; line-height: 1.25; min-width: 0; flex: 1 1 9em; hyphens: manual; color: var(--shop-heading, inherit); }
.tiq-shop__price { flex: none; font-size: 17px; font-weight: 700; color: var(--shop-price, inherit); }
.tiq-shop__text { margin: 0; font-size: 15px; line-height: 1.5; color: var(--shop-muted-text, inherit); opacity: 0.85; }
.tiq-shop__options { list-style: none; margin: 0; padding: 0; display: flex; flex-wrap: wrap; gap: 6px; }
.tiq-shop__options li {
    padding: 3px 10px;
    border: 1px solid var(--shop-line, rgba(0, 0, 0, 0.15));
    border-radius: 999px;
    font-size: 13px;
    font-weight: 600;
}
.tiq-shop__buy { margin-top: auto; padding-top: 6px; }
.tiq-shop__btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    width: 100%;
    padding: 11px 16px;
    border-radius: var(--shop-btn-radius, 8px);
    background: var(--shop-btn-bg, #1a1a1a);
    color: var(--shop-btn-text, #fff);
    font-weight: 700;
    font-size: 15px;
    text-decoration: none;
    box-sizing: border-box;
}
.tiq-shop__btn:hover { filter: brightness(0.94); }
.tiq-shop__btn.is-disabled { background: transparent; color: var(--shop-muted, #6b7280); border: 1px solid var(--shop-line, rgba(0, 0, 0, 0.15)); cursor: default; }
.tiq-shop__size { display: flex; align-items: center; gap: 10px; margin-bottom: 10px; }
.tiq-shop__size-label { font-size: 13px; font-weight: 600; color: var(--shop-muted, #6b7280); }
.tiq-shop__select {
    flex: 1;
    min-width: 0;
    padding: 8px 10px;
    border: 1px solid var(--shop-line, rgba(0, 0, 0, 0.2));
    border-radius: 8px;
    background: #fff;
    font: inherit;
    font-size: 15px;
}
.tiq-shop__select[aria-invalid="true"] { border-color: #b91c1c; }
.tiq-shop__btn--card { border: 0; cursor: pointer; font-family: inherit; }
.tiq-shop__warn { margin: 8px 0 0; font-size: 13px; color: #b91c1c; text-align: center; }
.tiq-shop__view {
    display: block;
    margin: 8px auto 0;
    border: 0;
    background: none;
    padding: 0;
    font: inherit;
    font-size: 14px;
    font-weight: 600;
    color: inherit;
    text-decoration: underline;
    cursor: pointer;
}
.tiq-shop__via { margin: 8px 0 0; text-align: center; font-size: 12px; color: var(--shop-muted, #6b7280); }
.tiq-shop__missing, .tiq-shop__hint { margin: 0; font-size: 13px; font-style: italic; color: var(--shop-muted, #6b7280); }
/* One column (a phone): a square photo the full width of the screen
   is a screenful per product, so it goes landscape. */
@container (max-width: 520px) {
    .tiq-shop__photo { aspect-ratio: 4 / 3; }
}
.tiq-shop__note { margin: 20px 0 0; font-size: 15px; color: var(--shop-muted, #6b7280); }
</style>
